// Composant d'entrée mémo (textarea) pour les formulaires U2000, avec design system unifié
// Inspiré de U2000InputText : même pattern avec gestion de la hauteur ou du nombre de lignes visibles
import React from "react";
import U2000Label from "../label/U2000Label";
import type { U2000FormStateHandlers } from "../../../reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../reducer/formReducer/U2000FormState";
import U2000InvalidFeedback from "../feedback/U2000InvalidFeedback";
import { u2000SelectInputContentsOnFocus } from "../../../tools/U2000FormInputUtils";

export interface U2000InputMemoProps {
  formStateHandlers: U2000FormStateHandlers<any>;
  fieldName: string;
  label: string;
  /**
   * Si true, le label n'est pas rendu par le composant.
   */
  hideLabel?: boolean;
  readonly?: boolean;
  required?: boolean;
  placeholder?: string;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  /** Nombre de lignes visibles (attribut rows du textarea). Défaut 3. */
  rows?: number;
  /** Hauteur minimale (valeur CSS, ex. "8rem", "120px"). Prioritaire sur rows si les deux sont fournis pour l'affichage. */
  minHeight?: string;
  /** Hauteur maximale (valeur CSS) pour le scroll. Optionnel. */
  maxHeight?: string;
}

const getTextareaClasses = (formStateHandlers: U2000FormStateHandlers<any>, fieldName: string, isFocused: boolean, readonly?: boolean) => {
  // BL-0349 : un seul fond par état (pas bg-surface + bg-muted)
  const baseClasses = "w-full px-4 py-3 text-sm border rounded-lg transition-all duration-200 focus:outline-hidden focus:ring-2 resize-y";
  const isDisabled = readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const hasError = (formStateHandlers.getTouched()[fieldName] && formStateHandlers.getErrors()[fieldName]) || (formStateHandlers.isSubmitted() && formStateHandlers.getErrors()[fieldName]);

  if (isDisabled) {
    return `${baseClasses} bg-muted text-gray-500 border-gray-200 cursor-not-allowed`;
  }

  if (hasError) {
    return `${baseClasses} bg-surface border-red-300 focus:border-red-500 focus:ring-red-500/20 text-gray-900 placeholder:text-gray-400`;
  }

  if (isFocused) {
    return `${baseClasses} bg-surface border-blue-500 ring-blue-500/20 text-gray-900 placeholder:text-gray-400`;
  }

  return `${baseClasses} bg-surface border-gray-300 focus:border-blue-500 focus:ring-blue-500/20 text-gray-900 placeholder:text-gray-400 hover:border-gray-400`;
};

function getNestedValue(formStateHandlers: U2000FormStateHandlers<any>, fieldName: string): any {
  const data = formStateHandlers.getData() as Record<string, any>;
  return fieldName.split(".").reduce((acc: any, part: string) => (acc !== undefined && acc !== null ? acc[part] : undefined), data);
}

const U2000InputMemo = ({
  formStateHandlers,
  fieldName,
  label,
  hideLabel = false,
  readonly = false,
  required = false,
  placeholder,
  onFocus,
  onBlur,
  textareaRef,
  rows = 3,
  minHeight,
  maxHeight,
}: U2000InputMemoProps) => {
  const [isFocused, setIsFocused] = React.useState(false);

  function handleFocus(e: React.FocusEvent<HTMLTextAreaElement>) {
    u2000SelectInputContentsOnFocus(e);
    setIsFocused(true);
    if (onFocus) onFocus(e);
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const newValue = e.target.value;
    if (formStateHandlers.onChange) {
      formStateHandlers.onChange(e as unknown as React.ChangeEvent<HTMLInputElement>, newValue);
    }
  }

  function handleBlur(e: React.FocusEvent<HTMLTextAreaElement>) {
    setIsFocused(false);
    if (formStateHandlers.onBlur) formStateHandlers.onBlur(e as unknown as React.FocusEvent<HTMLInputElement>);
    if (onBlur) onBlur(e);
  }

  const style: React.CSSProperties = {};
  if (minHeight) style.minHeight = minHeight;
  if (maxHeight) style.maxHeight = maxHeight;

  return (
    <div className="space-y-1.5">
      {!hideLabel && <U2000Label label={label} required={required} htmlFor={fieldName} />}

      <textarea
        ref={textareaRef}
        id={fieldName}
        name={fieldName}
        placeholder={placeholder}
        rows={rows}
        style={Object.keys(style).length > 0 ? style : undefined}
        className={getTextareaClasses(formStateHandlers, fieldName, isFocused, readonly)}
        value={getNestedValue(formStateHandlers, fieldName) ?? ""}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ}
        readOnly={readonly}
        required={required}
        aria-label={hideLabel ? label : undefined}
        aria-invalid={!!(formStateHandlers.getTouched()[fieldName] && formStateHandlers.getErrors()[fieldName])}
        aria-describedby={formStateHandlers.getErrors()[fieldName] ? `${fieldName}-error` : undefined}
      />

      <U2000InvalidFeedback formStateHandlers={formStateHandlers} fieldName={fieldName} />
    </div>
  );
};

export default U2000InputMemo;
